import { action, observable, reaction } from "mobx";
import { FormStore } from "@modules/formx";
import {
  CustomerContractSubVersionFragment,
  CustomerContractSubVersion_ById,
  CustomerContractInfo_ForSubVersions,
  CustomerContractSubVersion_SubmitForm,
  CustomerContractSubVersions_ByContractId,
  GetCustomerContractFiles
} from "@graphql";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { FormData } from "./utils";
import { FieldProps } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import * as f from "@contracts/CustomerContractProfile/components/SubVersionsTable/form/fields";

export { FormData };

export const defaultFields = [f.consultant, f.startDate, f.endDate, f.basicPrice];

export class SubVersionFormStore {
  form: FormStore<FormData>;
  subVersionId: string;
  customerContractId: string;
  handleClose: () => void;
  onSubmitCB: () => void;

  @observable loading = false;
  @observable isEdit = false;
  @observable isDuplicate = false;
  @observable alreadyUploadedDocument = false;
  @observable fileToUpload: File = null;
  @observable fields: FieldProps[] = defaultFields;
  @observable preFilledData: FormData = null;
  @observable
  customerContractName: string;

  constructor(
    initialValues: FormData,
    contractId: string,
    handleClose,
    subVersionId?: string,
    action?: string,
    onSubmitCB?: () => void
  ) {
    this.form = new FormStore<FormData>({
      onSubmit: this.onSubmit,
      initialValues
      // onSetValue: this._onSetValue
    });
    this.onSubmitCB = onSubmitCB;

    this.handleClose = handleClose;
    this.subVersionId = subVersionId;
    this.customerContractId = contractId;
    this.getSubVersion(subVersionId, contractId, action);

    this.isEdit = action === "edit";
    this.isDuplicate = action === "duplicate";

    reaction(
      () => this.preFilledData,
      (e) => {
        if (e) {
          this.form.reset(e);
        }
      }
    );
    (window as any).contractFormStore = this;
  }

  // _onSetValue = ({ name, value }) => {
  //   // change validation form when set ContractType to Ramavtal
  // };

  @action requestUpload = () =>
    store()
      .upload.requestFileUpload({
        multiple: false
      })
      .then((files) => {
        this.fileToUpload = files[0];
        this.form.setValue("fileName", files[0].name);
      });

  @action resetUpload = () => {
    this.fileToUpload = null;
    this.form.setValue("fileName", null);
  };

  @action setFormattedDataToForm = (data: CustomerContractSubVersionFragment) => {
    this.preFilledData = {
      basicPrice: data.basicPrice,
      startDate: data.startDate,
      endDate: data.endDate,
      consultantId: data.consultantId,
      fileName: data.fileRef?.Name
    };
    this.customerContractName = data.customerContractName;
  };

  @action setConsultant = (consultant) => {
    this.form.setValue("consultantId", consultant._id);
  };

  @action getSubVersion = async (subVersionId, customerContractId, action) => {
    this.loading = true;
    try {
      if (action === "add") {
        CustomerContractInfo_ForSubVersions.func({
          customerContractId
        }).then((res) => {
          this.customerContractName = res?.data?.customerContractById?.contractName;
        });
      } else if (action === "edit") {
        await CustomerContractSubVersion_ById.func(
          { subVersionId, customerContractId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          this.setFormattedDataToForm(res?.data?.customerContractSubVersionById);
          this.alreadyUploadedDocument =
            !!res?.data?.customerContractSubVersionById.fileRef?.NodeID;
        });
      } else if (action === "duplicate") {
        await CustomerContractSubVersion_ById.func(
          { subVersionId, customerContractId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          this.setFormattedDataToForm(res?.data?.customerContractSubVersionById);
        });
      }
    } catch (e) {
      store().snackbars.error(`${e}`);
    } finally {
      this.loading = false;
    }
  };

  download = (link: string) => {
    const a = document.createElement("a");
    a.href = link;
    a.click();
  };

  @action onSubmit = async () => {
    if (!this.form.valid) {
      return;
    }

    this.submit()
      .then((result) => {
        this.handleClose();
      })
      .catch((e) => {
        store().snackbars.error(`${e}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  @action submit = () => {
    const {
      values: { fileName, ...data }
    } = this.form.state;
    data.basicPrice = Number(data.basicPrice);

    this.loading = true;

    return CustomerContractSubVersion_SubmitForm.func(
      {
        customerContractId: this.customerContractId,
        subVersionId: this.isDuplicate ? null : this.subVersionId,
        data,
        file: this.fileToUpload
      },
      {
        refetchQueries: [
          CustomerContractSubVersions_ByContractId.OperationName,
          GetCustomerContractFiles.OperationName
        ]
      }
    ).then((res) => {
      store().snackbars.success(
        this.isEdit ? locale.EditContractSuccess : locale.AddContractSuccess
      );

      if (this.onSubmitCB) {
        this.onSubmitCB();
      }

      return res;
    });
  };
}
